import { FC, useState, useEffect } from "react"
import { Trash2, Grid3X3, List } from "lucide-react"
import { Button } from "@/components/ui/button"
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group"
import { motion, AnimatePresence } from "framer-motion"
import {
	Pagination,
	PaginationContent,
	PaginationEllipsis,
	PaginationItem,
	PaginationLink,
	PaginationNext,
	PaginationPrevious,
} from "@/components/ui/pagination"
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select"
import {
	usePaginatedDocuments,
	SortField,
	SortDirection,
} from "@/hooks/usePaginatedDocuments"
import { useBatchDocumentStages } from "@/hooks/useBatchDocumentStages"
import { DocumentCard } from "./DocumentCard"
import { DocumentsVerticalList } from "./DocumentsVerticalList"
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { TimedProgressBar } from "../TimedProgressBar"

// Page size options for different view modes
const PAGE_SIZE_OPTIONS = {
	grid: [3, 6, 9, 12, 15, 18, 20],
	table: [5, 10, 15, 20],
} as const

// Helper function to find the closest matching page size when switching modes
const findClosestPageSize = (
	currentSize: number,
	targetOptions: readonly number[]
): number => {
	return targetOptions.reduce((closest, option) => {
		const currentDiff = Math.abs(currentSize - closest)
		const optionDiff = Math.abs(currentSize - option)
		return optionDiff < currentDiff ? option : closest
	})
}

interface PaginatedDocumentsBrowserProps {
	caseId: string
	onDeleteDocument: (documentId: string) => Promise<void>
	onBatchDeleteDocuments: (documentIds: string[]) => Promise<void>
	searchQuery?: string
	includeIrrelevant?: boolean
}

export const PaginatedDocumentsBrowser: FC<PaginatedDocumentsBrowserProps> = ({
	caseId,
	onDeleteDocument,
	onBatchDeleteDocuments,
	searchQuery,
	includeIrrelevant = false,
}) => {
	// Pagination state
	const [currentPage, setCurrentPage] = useState(1)
	const [pageSize, setPageSize] = useState(6)
	const [viewMode, setViewMode] = useState<"grid" | "table">("grid")

	// Sorting state
	const [sortField, setSortField] = useState<SortField>("name")
	const [sortDirection, setSortDirection] = useState<SortDirection>("asc")

	// Selection state for batch delete
	const [deleteMultipleSelected, setDeleteMultipleSelected] = useState(false)
	const [selectedDocumentIds, setSelectedDocumentIds] = useState<Set<string>>(
		new Set()
	)
	const [isBatchDeleting, setIsBatchDeleting] = useState(false)

	// Track deleted document IDs to hide them optimistically
	const [deletedDocumentIds, setDeletedDocumentIds] = useState<Set<string>>(
		new Set()
	)

	const { data, isLoading, error, refetch } = usePaginatedDocuments({
		caseId,
		page: currentPage,
		limit: pageSize,
		relevantOnly: false,
		sortBy: sortField,
		sortDirection,
		searchQuery,
		includeIrrelevant,
	})

	// Filter out deleted documents
	const filteredDocuments =
		data?.documents.filter((doc) => !deletedDocumentIds.has(doc.id)) || []

	// Fetch stages for all visible documents
	const documentIds = filteredDocuments.map((doc) => doc.id)
	const { data: stagesData } = useBatchDocumentStages(caseId, documentIds)

	const handleDeleteMultipleSelected = (documentId: string) => {
		setDeleteMultipleSelected(true)
		setSelectedDocumentIds(new Set([documentId]))
	}

	const handleSelectDocument = (documentId: string) => {
		if (!deleteMultipleSelected) {
			return
		}
		setSelectedDocumentIds((prevSelected) => {
			const newSelected = new Set(prevSelected)
			if (newSelected.has(documentId)) {
				newSelected.delete(documentId)
			} else {
				newSelected.add(documentId)
			}
			return newSelected
		})
	}

	const handleConfirmBatchDelete = async () => {
		setIsBatchDeleting(true)
		const idsToDelete = Array.from(selectedDocumentIds)
		try {
			// Optimistically hide documents
			setDeletedDocumentIds((prev) => new Set([...prev, ...idsToDelete]))
			await onBatchDeleteDocuments(idsToDelete)
			setSelectedDocumentIds(new Set())
			setDeleteMultipleSelected(false)
			refetch()
		} catch (error) {
			console.error("Batch delete failed:", error)
			// Remove from deleted set if failed
			setDeletedDocumentIds((prev) => {
				const newSet = new Set(prev)
				idsToDelete.forEach((id) => newSet.delete(id))
				return newSet
			})
		} finally {
			setIsBatchDeleting(false)
		}
	}

	const handlePageChange = (page: number) => {
		setCurrentPage(page)
		setSelectedDocumentIds(new Set())
		setDeleteMultipleSelected(false)
	}

	const handleSort = (field: SortField) => {
		if (sortField === field) {
			// Toggle direction if same field
			setSortDirection(sortDirection === "asc" ? "desc" : "asc")
		} else {
			// Set new field and default to ascending
			setSortField(field)
			setSortDirection("asc")
		}
		// Reset to first page when sorting changes
		setCurrentPage(1)
	}

	const handlePageSizeChange = (value: string) => {
		setPageSize(Number(value))
		setCurrentPage(1)
		setSelectedDocumentIds(new Set())
		setDeleteMultipleSelected(false)
	}

	const handleDeleteDocument = async (documentId: string) => {
		try {
			// Optimistically hide document
			setDeletedDocumentIds((prev) => new Set([...prev, documentId]))
			await onDeleteDocument(documentId)
			refetch()
		} catch (error) {
			console.error("Delete failed:", error)
			// Remove from deleted set if failed
			setDeletedDocumentIds((prev) => {
				const newSet = new Set(prev)
				newSet.delete(documentId)
				return newSet
			})
		}
	}

	const selectedDocuments = filteredDocuments.filter((doc) =>
		selectedDocumentIds.has(doc.id)
	)
	const numSelected = selectedDocumentIds.size

	if (isLoading && !data) {
		return (
			<div className="flex w-full flex-col items-center justify-center gap-4 py-12">
				<p className="text-sm text-muted-foreground">Analyzing documents...</p>
				<TimedProgressBar duration={12000} />
			</div>
		)
	}

	if (error) {
		return (
			<div className="text-center py-12 text-destructive">
				Error loading documents: {error.message}
			</div>
		)
	}

	if (!data || filteredDocuments.length === 0) {
		return (
			<div className="text-center py-12 border rounded-xl border-dashed">
				<p className="text-muted-foreground">No documents found</p>
			</div>
		)
	}

	// Generate page numbers for pagination
	const generatePageNumbers = () => {
		const pages = []
		const totalPages = data.total_pages
		const current = currentPage

		if (totalPages <= 7) {
			// Show all pages if 7 or less
			for (let i = 1; i <= totalPages; i++) {
				pages.push(i)
			}
		} else {
			// Always show first page
			pages.push(1)

			if (current > 3) {
				pages.push(-1) // Ellipsis
			}

			// Show pages around current
			for (
				let i = Math.max(2, current - 1);
				i <= Math.min(totalPages - 1, current + 1);
				i++
			) {
				pages.push(i)
			}

			if (current < totalPages - 2) {
				pages.push(-1) // Ellipsis
			}

			// Always show last page
			pages.push(totalPages)
		}

		return pages
	}

	return (
		<div>
			{/* Search Results Header */}
			{searchQuery && (
				<div className="mb-4">
					<h3 className="text-lg font-medium">Search Results</h3>
					<p className="text-sm text-muted-foreground">
						Searching for "{searchQuery}" - Found {data?.total || 0} documents
					</p>
				</div>
			)}

			{/* Pagination controls at top */}
			<div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-6">
				<div className="flex items-center gap-2">
					<span className="text-sm text-muted-foreground">Items per page:</span>
					<Select
						value={pageSize.toString()}
						onValueChange={handlePageSizeChange}
					>
						<SelectTrigger className="w-[70px]">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							{PAGE_SIZE_OPTIONS[viewMode].map((size) => (
								<SelectItem key={size} value={size.toString()}>
									{size}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>

				<Pagination>
					<PaginationContent>
						<PaginationItem>
							<PaginationPrevious
								onClick={() => handlePageChange(currentPage - 1)}
								className={
									currentPage === 1
										? "pointer-events-none opacity-50"
										: "cursor-pointer"
								}
							/>
						</PaginationItem>

						{generatePageNumbers().map((pageNum, idx) =>
							pageNum === -1 ? (
								<PaginationItem key={`ellipsis-${idx}`}>
									<PaginationEllipsis />
								</PaginationItem>
							) : (
								<PaginationItem key={pageNum}>
									<PaginationLink
										onClick={() => handlePageChange(pageNum)}
										isActive={pageNum === currentPage}
										className="cursor-pointer"
									>
										{pageNum}
									</PaginationLink>
								</PaginationItem>
							)
						)}

						<PaginationItem>
							<PaginationNext
								onClick={() => handlePageChange(currentPage + 1)}
								className={
									currentPage === data.total_pages
										? "pointer-events-none opacity-50"
										: "cursor-pointer"
								}
							/>
						</PaginationItem>
					</PaginationContent>
				</Pagination>

				{/* View mode toggle */}
				<ToggleGroup
					type="single"
					value={viewMode}
					onValueChange={(value) => {
						if (value && value !== viewMode) {
							const newMode = value as "grid" | "table"
							setViewMode(newMode)

							// Adjust page size to closest matching value in new mode
							const newPageSize = findClosestPageSize(
								pageSize,
								PAGE_SIZE_OPTIONS[newMode]
							)
							if (newPageSize !== pageSize) {
								setPageSize(newPageSize)
								setCurrentPage(1)
							}
						}
					}}
					className="border rounded-md p-1"
				>
					<ToggleGroupItem value="grid" aria-label="Grid view" className="px-3">
						<Grid3X3 className="h-4 w-4" />
					</ToggleGroupItem>
					<ToggleGroupItem
						value="table"
						aria-label="Table view"
						className="px-3"
					>
						<List className="h-4 w-4" />
					</ToggleGroupItem>
				</ToggleGroup>
			</div>

			{/* Header with delete controls */}
			<div className="mb-4 flex justify-between items-center">
				<div className="text-sm text-muted-foreground">
					Showing {(currentPage - 1) * pageSize + 1}-
					{Math.min(currentPage * pageSize, data.total)} of {data.total}{" "}
					documents
				</div>

				{deleteMultipleSelected && (
					<div className="flex items-center gap-2">
						<AlertDialog>
							<AlertDialogTrigger asChild>
								<Button variant="destructive" disabled={isBatchDeleting}>
									<Trash2 className="mr-2 h-4 w-4" />
									Delete Selected ({numSelected}){isBatchDeleting && "..."}
								</Button>
							</AlertDialogTrigger>
							<AlertDialogContent>
								<AlertDialogHeader>
									<AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
									<AlertDialogDescription>
										This action cannot be undone. This will permanently delete
										the following {numSelected} document(s):
										<ul className="mt-2 list-disc list-inside max-h-60 overflow-y-auto">
											{selectedDocuments.map((doc) => (
												<li key={doc.id} className="truncate">
													{doc.name}
												</li>
											))}
										</ul>
									</AlertDialogDescription>
								</AlertDialogHeader>
								<AlertDialogFooter>
									<AlertDialogCancel disabled={isBatchDeleting}>
										Cancel
									</AlertDialogCancel>
									<AlertDialogAction
										onClick={handleConfirmBatchDelete}
										disabled={isBatchDeleting}
										className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
									>
										{isBatchDeleting ? "Deleting..." : "Yes, delete"}
									</AlertDialogAction>
								</AlertDialogFooter>
							</AlertDialogContent>
						</AlertDialog>
						<Button
							variant="outline"
							onClick={() => {
								setDeleteMultipleSelected(false)
								setSelectedDocumentIds(new Set())
							}}
						>
							Cancel
						</Button>
					</div>
				)}
			</div>

			{/* Document view with animated height */}
			<motion.div
				transition={{ duration: 0.3, ease: "easeInOut" }}
				className="mb-6"
			>
				<AnimatePresence>
					{viewMode === "grid" ? (
						<motion.div
							key={`grid-${currentPage}`}
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
							transition={{ duration: 0.2 }}
							className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-auto items-start"
						>
							{filteredDocuments.map((document, index) => (
								<DocumentCard
									key={document.id}
									document={document}
									caseId={caseId}
									onDelete={() => handleDeleteDocument(document.id)}
									onDeleteMultipleSelected={() =>
										handleDeleteMultipleSelected(document.id)
									}
									isSelected={selectedDocumentIds.has(document.id)}
									onSelect={() => handleSelectDocument(document.id)}
									index={index}
									stagesData={stagesData?.[document.id]}
								/>
							))}
						</motion.div>
					) : (
						<motion.div
							key={`table-${currentPage}`}
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
							transition={{ duration: 0.2 }}
							className="w-full"
						>
							<DocumentsVerticalList
								documents={filteredDocuments}
								caseId={caseId}
								onDeleteDocument={handleDeleteDocument}
								onBatchDeleteDocuments={onBatchDeleteDocuments}
								stagesData={stagesData}
								sortField={sortField}
								sortDirection={sortDirection}
								onSort={handleSort}
							/>
						</motion.div>
					)}
				</AnimatePresence>
			</motion.div>
		</div>
	)
}
